import React, { useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import HeroAgendaPreview from '../../components/marketing/HeroAgendaPreview';
import LandingCtaButton from '../../components/marketing/LandingCtaButton';
import LandingHeader from '../../components/marketing/LandingHeader';
import PlanCard from '../../components/marketing/PlanCard';
import SalovareMonogram from '../../components/common/brand/SalovareMonogram';
import SalovareWordmark from '../../components/common/brand/SalovareWordmark';
import SectionMasthead from '../../components/marketing/SectionMasthead';
import landingTheme from '../../components/marketing/landingTheme';
import {
  LANDING_COLORS,
  hairlineGridSx,
  landingBodySx,
  landingEyebrowSx,
  landingSectionSx,
} from '../../components/marketing/landingTokens';
import LegalFooterLinks from '../../components/legal/LegalFooterLinks';
import PageSeo from '../../components/seo/PageSeo';
import MarketingLanguageLinks from '../../components/seo/MarketingLanguageLinks';
import useLocalizedMarketingPath from '../../hooks/useLocalizedMarketingPath';
import { normalizeLanguage } from '../../constants/Languages';
import { LANDING_PRICING } from '../../constants/LandingPricing';
import {
  FEATURE_KEYS,
  HERO_SPEC_KEYS,
  STEP_KEYS,
  buildFaqItems,
  buildLandingJsonLd,
  buildPlanRows,
  buildPricingFootnote,
  getLandingPrices,
} from './landingContent';

const FEATURE_ICONS = {
  appointments: CalendarMonthOutlinedIcon,
  booking: StorefrontOutlinedIcon,
  team: GroupsOutlinedIcon,
  services: ContentCutOutlinedIcon,
  memberships: CardMembershipOutlinedIcon,
  customers: ContactsOutlinedIcon,
  reminders: SmsOutlinedIcon,
  payroll: AccountBalanceWalletOutlinedIcon,
  dashboard: InsightsOutlinedIcon,
};

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const localizePath = useLocalizedMarketingPath();
  const { isManagementAuthenticated } = useAuth();
  const lang = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const registrationPath = localizePath('/register-store');
  const goToControlPanel = () => navigate('/management');
  const goToRegistration = () => navigate(registrationPath);
  const primaryAction = isManagementAuthenticated ? goToControlPanel : goToRegistration;

  const prices = getLandingPrices();
  const { introPrice, basePrice, teamMemberPrice, extraStorePrice, smsPrice, whatsAppPrice } = prices;

  const planRows = useMemo(() => {
    const messageRates = { sms: smsPrice, whatsapp: whatsAppPrice };
    return {
      free: buildPlanRows('free', t, messageRates),
      pro: buildPlanRows('pro', t, messageRates),
    };
  }, [t, smsPrice, whatsAppPrice]);

  const faqItems = buildFaqItems(t, prices);
  const jsonLd = buildLandingJsonLd(t, lang, faqItems);

  return (
    <ThemeProvider theme={landingTheme}>
      <Box sx={{ width: '100%', overflowX: 'hidden', background: LANDING_COLORS.sand, color: LANDING_COLORS.charcoal }}>
        <PageSeo
          title={t('landing.seo.title')}
          description={t('landing.seo.description')}
          path="/"
          jsonLd={jsonLd}
        />
        <LandingHeader />

        <Box component="main">
          {/* Hero */}
          <Box
            sx={{
              pt: { xs: 12, md: 17 },
              pb: { xs: 7, md: 10 },
              background: LANDING_COLORS.navy,
              color: LANDING_COLORS.onDark,
            }}
          >
            <Container maxWidth="lg">
              <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
                <Grid size={{ xs: 12, md: 7 }}>
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: '-0.03em',
                      lineHeight: 1.06,
                      color: LANDING_COLORS.onDark,
                      fontSize: { xs: '2.2rem', sm: '2.6rem', md: '2.9rem', lg: '3.2rem' },
                      maxWidth: 620,
                    }}
                  >
                    {t('landing.hero.title')}
                  </Typography>

                  <Typography
                    component="p"
                    sx={{
                      ...landingBodySx,
                      mt: 3,
                      maxWidth: 560,
                      color: LANDING_COLORS.onDarkMuted,
                      fontSize: { xs: '1rem', md: '1.08rem' },
                    }}
                  >
                    {t('landing.hero.subtitle')}
                  </Typography>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ mt: 4, width: { xs: '100%', sm: 'auto' } }}
                  >
                    {isManagementAuthenticated ? (
                      <LandingCtaButton size="large" onClick={goToControlPanel} sx={{ px: 4, py: 1.5 }}>
                        {t('landing.nav.goToControlPanel')}
                      </LandingCtaButton>
                    ) : (
                      <>
                        <LandingCtaButton size="large" component={RouterLink} to={registrationPath} sx={{ px: 4, py: 1.5 }}>
                          {t('landing.hero.ctaPrimary')}
                        </LandingCtaButton>
                        <Button
                          size="large"
                          variant="outlined"
                          onClick={() => navigate('/management/team-member/login')}
                          sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: '10px',
                            borderColor: 'rgba(245, 242, 238, 0.32)',
                            color: LANDING_COLORS.onDark,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1rem',
                            '&:hover': {
                              borderColor: LANDING_COLORS.terracottaLight,
                              background: 'rgba(245, 242, 238, 0.06)',
                            },
                          }}
                        >
                          {t('landing.hero.ctaSecondary')}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }} sx={{ position: 'relative' }}>
                  <SalovareMonogram
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      width: 380,
                      top: '50%',
                      right: -150,
                      transform: 'translateY(-50%) rotate(-8deg)',
                      color: LANDING_COLORS.terracotta,
                      opacity: 0.9,
                    }}
                  />
                  <HeroAgendaPreview />
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: { xs: 6, md: 9 },
                  borderTop: `1px solid ${LANDING_COLORS.onDarkLine}`,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                  columnGap: { xs: 3, md: 0 },
                }}
              >
                {HERO_SPEC_KEYS.map((key, index) => (
                  <Box
                    key={key}
                    sx={{
                      py: 2.5,
                      borderLeft: { xs: 'none', md: index === 0 ? 'none' : `1px solid ${LANDING_COLORS.onDarkLine}` },
                      pl: { xs: 0, md: index === 0 ? 0 : 3 },
                    }}
                  >
                    <Typography component="span" sx={{ ...landingEyebrowSx, color: LANDING_COLORS.onDarkMuted }}>
                      {t(`landing.hero.specs.${key}.label`)}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mt: 0.5 }}>
                      {t(`landing.hero.specs.${key}.value`, { price: introPrice })}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Container>
          </Box>

          {/* Features */}
          <Box id="features" sx={{ ...landingSectionSx, background: LANDING_COLORS.paper }}>
            <Container maxWidth="lg">
              <SectionMasthead
                title={t('landing.features.title')}
                subtitle={t('landing.features.subtitle')}
              />

              <Box
                sx={hairlineGridSx({
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                })}
              >
                {FEATURE_KEYS.map((key) => {
                  const Icon = FEATURE_ICONS[key];
                  return (
                  <Box key={key} sx={{ p: { xs: 3, md: 3.5 } }}>
                    <Icon sx={{ fontSize: 24, color: LANDING_COLORS.terracotta }} />
                    <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1rem', mt: 1.5, mb: 1, color: LANDING_COLORS.navy }}>
                      {t(`landing.features.${key}.title`)}
                    </Typography>
                    <Typography sx={{ ...landingBodySx, fontSize: '0.88rem' }}>
                      {t(`landing.features.${key}.description`)}
                    </Typography>
                  </Box>
                  );
                })}
              </Box>
            </Container>
          </Box>

          {/* How it works */}
          <Box id="how-it-works" sx={{ ...landingSectionSx, background: LANDING_COLORS.sand }}>
            <Container maxWidth="lg">
              <SectionMasthead
                title={t('landing.howItWorks.title')}
                subtitle={t('landing.howItWorks.subtitle')}
              />

              <Box
                sx={hairlineGridSx(
                  { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                  LANDING_COLORS.sand,
                )}
              >
                {STEP_KEYS.map((key, index) => (
                  <Box key={key} sx={{ p: { xs: 3, md: 3.5 } }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.4rem',
                        letterSpacing: '-0.02em',
                        color: LANDING_COLORS.terracotta,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </Typography>
                    <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1rem', mt: 1.5, mb: 1, color: LANDING_COLORS.navy }}>
                      {t(`landing.howItWorks.steps.${key}.title`)}
                    </Typography>
                    <Typography sx={{ ...landingBodySx, fontSize: '0.88rem' }}>
                      {t(`landing.howItWorks.steps.${key}.description`)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Container>
          </Box>

          {/* Pricing */}
          <Box id="pricing" sx={{ ...landingSectionSx, background: LANDING_COLORS.paper }}>
            <Container maxWidth="lg">
              <SectionMasthead
                title={t('landing.pricing.title')}
                subtitle={t('landing.pricing.subtitle')}
              />

              <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
                <Grid size={{ xs: 12, md: 6 }}>
                  <PlanCard
                    name={t('landing.pricing.plans.free.name')}
                    tagline={t('landing.pricing.plans.free.tagline')}
                    price={t('landing.pricing.plans.free.price')}
                    priceNote={t('landing.pricing.plans.free.priceNote')}
                    rows={planRows.free}
                    note={t('landing.pricing.plans.free.note')}
                    ctaLabel={
                      isManagementAuthenticated
                        ? t('landing.nav.goToControlPanel')
                        : t('landing.pricing.plans.free.cta')
                    }
                    onCtaClick={primaryAction}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PlanCard
                    dark
                    name={t('landing.pricing.plans.pro.name')}
                    tagline={t('landing.pricing.plans.pro.tagline')}
                    price={introPrice}
                    priceSuffix={t('landing.pricing.plans.pro.priceSuffix')}
                    priceNote={t('landing.pricing.plans.pro.priceNote', {
                      price: basePrice,
                      months: LANDING_PRICING.introductoryPeriodMonths,
                    })}
                    rows={planRows.pro}
                    addOnsTitle={t('landing.pricing.addOns.title')}
                    addOns={[
                      {
                        label: t('landing.pricing.addOns.additionalStore'),
                        value: t('landing.pricing.addOns.perMonth', { price: extraStorePrice }),
                      },
                      {
                        label: t('landing.pricing.addOns.additionalProfessional'),
                        value: t('landing.pricing.addOns.perMonth', { price: teamMemberPrice }),
                      },
                    ]}
                    comingSoonLabel={t('landing.pricing.comingSoon')}
                    ctaLabel={
                      isManagementAuthenticated
                        ? t('landing.nav.goToControlPanel')
                        : t('landing.pricing.plans.pro.cta')
                    }
                    onCtaClick={primaryAction}
                  />
                </Grid>
              </Grid>

              <Typography
                sx={{
                  mt: 4,
                  fontSize: '0.78rem',
                  color: LANDING_COLORS.muted,
                  maxWidth: 820,
                  lineHeight: 1.7,
                }}
              >
                {buildPricingFootnote(t, prices)}
              </Typography>
            </Container>
          </Box>

          {/* FAQ */}
          <Box id="faq" sx={{ ...landingSectionSx, background: LANDING_COLORS.sand }}>
            <Container maxWidth="lg">
              <Grid container spacing={{ xs: 2, md: 8 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ position: { md: 'sticky' }, top: 104 }}>
                    <SectionMasthead
                      layout="stacked"
                      title={t('landing.faq.title')}
                      subtitle={t('landing.faq.subtitle')}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  {faqItems.map((item) => (
                    <Accordion
                      key={item.question}
                      disableGutters
                      square
                      elevation={0}
                      sx={{
                        background: 'transparent',
                        borderTop: `1px solid ${LANDING_COLORS.line}`,
                        '&:last-of-type': { borderBottom: `1px solid ${LANDING_COLORS.line}` },
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: LANDING_COLORS.terracotta, fontSize: 22 }} />}
                        sx={{ px: 0, py: 1.25 }}
                      >
                        <Typography component="h3" sx={{ fontWeight: 600, fontSize: '1rem', pr: 2, color: LANDING_COLORS.navy }}>
                          {item.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0, pb: 3 }}>
                        <Typography sx={{ ...landingBodySx, fontSize: '0.92rem', maxWidth: 640 }}>
                          {item.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Closing call to action */}
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              background: LANDING_COLORS.terracotta,
              color: LANDING_COLORS.onDark,
              py: { xs: 7, md: 10 },
            }}
          >
            <SalovareMonogram
              sx={{
                position: 'absolute',
                width: { xs: 260, md: 420 },
                right: { xs: -90, md: '4%' },
                bottom: { xs: -70, md: -120 },
                color: LANDING_COLORS.terracottaDeep,
                opacity: 0.55,
              }}
            />
            <Container maxWidth="lg" sx={{ position: 'relative' }}>
              <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography
                    variant="h3"
                    component="p"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.15,
                      fontSize: { xs: '1.7rem', md: '2.3rem' },
                      maxWidth: 620,
                    }}
                  >
                    {t('landing.cta.title')}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 2,
                      color: LANDING_COLORS.onDark,
                      lineHeight: 1.75,
                      maxWidth: 520,
                      fontSize: { xs: '0.95rem', md: '1rem' },
                    }}
                  >
                    {t('landing.cta.subtitle')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: { md: 'flex-end' } }}>
                  <LandingCtaButton
                    size="large"
                    tone="light"
                    onClick={primaryAction}
                    sx={{ px: 4.5, py: 1.5, fontSize: '1rem' }}
                  >
                    {isManagementAuthenticated ? t('landing.nav.goToControlPanel') : t('landing.cta.button')}
                  </LandingCtaButton>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Footer */}
          <Box
            component="footer"
            sx={{ background: LANDING_COLORS.paper, borderTop: `1px solid ${LANDING_COLORS.line}`, py: 4 }}
          >
            <Container maxWidth="lg">
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <SalovareWordmark title={t('brand')} sx={{ height: 24 }} />
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: LANDING_COLORS.muted,
                    textAlign: 'center',
                    order: { xs: 3, sm: 2 },
                  }}
                >
                  {t('landing.footer.copyright', { year: new Date().getFullYear() })}
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ order: { xs: 2, sm: 3 } }}>
                  {isManagementAuthenticated ? (
                    <Button onClick={goToControlPanel} sx={{ color: LANDING_COLORS.terracotta, textTransform: 'none', fontWeight: 600 }}>
                      {t('landing.nav.goToControlPanel')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => navigate('/management/team-member/login')}
                        sx={{ color: LANDING_COLORS.muted, textTransform: 'none', fontWeight: 600 }}
                      >
                        {t('landing.footer.signIn')}
                      </Button>
                      <Button
                        component={RouterLink}
                        to={registrationPath}
                        sx={{ color: LANDING_COLORS.terracotta, textTransform: 'none', fontWeight: 600 }}
                      >
                        {t('landing.footer.register')}
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <LegalFooterLinks />
                <MarketingLanguageLinks path="/" />
              </Box>
            </Container>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LandingPage;
